import React, { useEffect, useState } from 'react';
import { DialogUI, FormButtonPanelUI } from '../../Interface';
import Yup from '../../../utils/yup';
import { Form, Formik } from 'formik';
import useUsers from '../../../context/UsersContext';
import useConfig from '../../../context/ConfigContext';
import { AddressType, BpType } from '../../../config/company';
import CompanyAddDialogTabs from './CompanyAddDialogTabs';
import masterDataService from '../../../service/masterData.service';
import { GroupType } from '../../../config/companyGroup';
import { NotificationType } from '../../../config';
import addNotification from '../../../utils/addNotification';
import companyService from '../../../service/company.service';
import useParam from '../../../context/ParamContext';
import { Box, IconButton, Tooltip } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const CompanyAddDialog = ({ open, handleClose, onCreated, onSwitchToDetailed, customer = true, supplier = false }) => {
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { params, getParam } = useParam();
  const [customerGroups, setCustomerGroups] = useState([]);

  const initialValues = {
    name: '',
    hunTaxNumber: '',
    bpType: BpType.COMPANY,
    address: [
      {
        postCode: '',
        city: '',
        streetAddress: '',
      },
    ],
    contact: [
      {
        name: '',
        phoneNumber: '',
        email: '',
        role: '',
      },
    ],
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    if (!Object.keys(params).length) getParam();
    getCompanyGroups();
  }, []);

  const getCompanyGroups = () => {
    masterDataService.companyGroupToForm().then((data) => {
      const filteredCompanyGroup = data.filter((item) => item.groupType === GroupType.CUSTOMERGROUP);

      setCustomerGroups(filteredCompanyGroup);
    });
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
    hunTaxNumber: Yup.string()
      .nullable()
      .matches(/^\d{8}(?:-\d{1,2}-\d{2})?$/, {
        message: 'A magyar adószám formátuma helytelen. Helyes formátum például: 12345678 vagy 12345678-1-23',
        excludeEmptyString: true,
      }),
    bpType: Yup.string().nullable(),
    address: Yup.array().of(
      Yup.object().shape({
        postCode: Yup.string().nullable(),
        city: Yup.string().nullable(),
        streetAddress: Yup.string().nullable(),
      })
    ),
    contact: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .nullable()
          .when(['phoneNumber', 'email'], {
            is: (phoneNumber, email) => !!phoneNumber || !!email,
            then: (schema) => schema.required(),
          }),
        phoneNumber: Yup.string().nullable(),
        email: Yup.string().nullable(),
        role: Yup.string().nullable(),
      })
    ),
  });

  const handleInsertObject = async (values) => {
    const address = values.address[0];
    const contact = values.contact[0];

    const hasAddress = address.postCode || address.city || address.streetAddress;
    const hasContact = contact.name || contact.phoneNumber || contact.email;

    const customerGroupId = customer ? config.defCompanyCustomerGroup ?? null : null;

    const foundCustomerGroup = customerGroups.find((group) => group.id === customerGroupId);

    const insertObject = {
      ...values,
      createdBy: user.userId,
      customer,
      supplier,
      languageCode: config.defCompanyLanguage ?? config.countryCode ?? null,
      customerGroupId,
      customerCurrencyCode: customer ? foundCustomerGroup?.currencyCode ?? null : null,
      customerPriceListId: customer ? foundCustomerGroup?.priceListId ?? null : null,
      supplierCurrencyCode: supplier ? config.currencyCode ?? null : null,
      contact: hasContact
        ? [
            {
              ...contact,
              role: contact.role || 'Kapcsolattartó',
              default: true,
              active: true,
              createdBy: user.userId,
            },
          ]
        : [],
      address: hasAddress
        ? [
            {
              ...address,
              name: config.defCompanyBillingAddr ?? 'Számlázási cím',
              adressType: AddressType.BILLING,
              countryCode: address.countryCode ?? config.defCompanyCountry ?? null,
              default: true,
              active: true,
              createdBy: user.userId,
            },
            {
              ...address,
              name: config.defCompanyShippingAddr ?? 'Szállítási cím',
              adressType: AddressType.SHIPPING,
              countryCode: address.countryCode ?? config.defCompanyCountry ?? null,
              default: true,
              active: true,
              createdBy: user.userId,
            },
          ]
        : [],
    };

    companyService
      .createCompany(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        handleClose();
        onCreated(data);
      })
      .finally(() => {
        //@TODO loader
      });
  };

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      headerContent={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>Új partner felvétel</span>
          {onSwitchToDetailed && (
            <Tooltip title="Váltás a részletes partnerfelvételre">
              <IconButton size="small" color="inherit" onClick={onSwitchToDetailed}>
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1200px',
            mx: 0,
          },
        },
        '& .MuiDialogContent-root': {
          px: 1,
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        onSubmit={handleInsertObject}
        enableReinitialize
      >
        {({}) => (
          <Form>
            <CompanyAddDialogTabs />

            <FormButtonPanelUI
              disableTabFocus
              onCancelClick={() => {
                handleClose();
              }}
            />
          </Form>
        )}
      </Formik>
    </DialogUI>
  );
};

export default CompanyAddDialog;
